import React, { useEffect, useRef, useState } from "react";
import { Control, Controller, useFormContext } from "react-hook-form";
import { useAppSelector } from "@/hooks";

import { CountryDropdown, Image, Input, ErrorUI } from "@/components/Atoms";
import { formatOptionLabelV2 } from "@/components/Molecules/FormatOptionLabel/V2";

import { isValidMobile, matchCountryRegex } from "@/utils";
import Typography from "@/foundation/Typography/V2";
import theme from "@/foundation/Theme";

import {
  ArrowDown,
  CountryPlaceholder,
  DropDown,
  InputWrap,
  NumberPlaceholder,
  PhoneFlag,
  PhoneIcon,
} from "./style";
import {
  InputGroupV2,
  InputWrapperV2,
  InputGroupV3,
  InputWrapperV3,
} from "../style";

interface PhoneInputProps {
  name: string;
  control: Control<any>;
  mobilePlaceholder: string;
  showLabels?: boolean;
  inputFontSize?: string;
  inputLineHeight?: string;
  isNotAbsolute?: boolean;
  handleBlur?: (e: any) => void;
  showPhoneIcon?: boolean;
  showIcon?: boolean;
  variant?: "v1" | "v2" | "v3";
}

const PhoneInput: React.FC<PhoneInputProps> = ({
  name,
  control,
  showLabels,
  mobilePlaceholder,
  inputFontSize,
  inputLineHeight,
  showPhoneIcon = false,
  showIcon = false,
  handleBlur = () => {},
  isNotAbsolute,
  variant = "v3",
}) => {
  const { countryCodeData: countryCodes } = useAppSelector(
    (state) => state.dashboard
  );

  const [showCountryDropDown, setShowCountryDropDown] = useState(false);

  function useOutsideAlerter(ref) {
    useEffect(() => {
      function handleClickOutside(event) {
        if (ref.current && !ref.current.contains(event.target)) {
          setShowCountryDropDown(false);
        }
      }
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }, [ref]);
  }

  const wrapperRef = useRef(null);

  useOutsideAlerter(wrapperRef);

  const { getValues } = useFormContext();
  const values = getValues();

  let inputController = (
    <Controller
      name={name}
      control={control}
      shouldUnregister
      render={({
        field: { value, onBlur, onChange, ref },
        fieldState: { error },
        formState: { errors },
      }) => (
        <>
          <InputWrap>
            <Controller
              name={`${name}CountryCode`}
              control={control}
              shouldUnregister
              render={({ field: { value, onChange, ref }, formState: {} }) => (
                <CountryPlaceholder showCountryDropDown={showCountryDropDown}>
                  <NumberPlaceholder
                    onClick={() => setShowCountryDropDown(!showCountryDropDown)}
                    position="relative"
                    className="countryCodePlaceholder"
                  >
                    {/* {!showPhoneIcon &&
                    (value === "+91" ||
                      value === "+971" ||
                      value === "+974" ||
                      value === "+1" ||
                      value === "+65" ||
                      value === "+44") ? (
                      <PhoneFlag height="16px" width="24px">
                        <Image
                          maxWidth="24px"
                          maxHeight="16px"
                          imageName={`flags/${
                            value === "+91"
                              ? "india.png"
                              : value === "+971"
                              ? "uae.png"
                              : value === "+974"
                              ? "qatar.png"
                              : value === "+1"
                              ? "usa.png"
                              : value === "+65"
                              ? "singapore.png"
                              : value === "+44" && "uk.png"
                          }`}
                          alt="flag"
                        />
                      </PhoneFlag>
                    ) : (
                      <PhoneIcon
                        icon="icon-phone-2"
                        fill={theme.colorsV2.gray.shade_700}
                        height="16px"
                        width="16px"
                      />
                    )} */}
                    {(showPhoneIcon || showIcon) && (
                      <PhoneIcon
                        icon="icon-phone-2"
                        fill={theme.colorsV2.gray.shade_700}
                        height="16px"
                        width="16px"
                      />
                    )}
                    <Typography
                      variant="label2"
                      color={theme.colorsV2.gray.shade_700}
                      width="100%"
                      textAlign="center"
                    >
                      {value}
                    </Typography>
                    <ArrowDown
                      icon="icon-caret-down"
                      fill={theme.colorsV2.gray.shade_700}
                      height="8px"
                      width="8px"
                    />
                  </NumberPlaceholder>
                  <DropDown
                    showCountryDropDown={showCountryDropDown}
                    ref={wrapperRef}
                  >
                    {/* FATAL "check for element which are not from yup" */}
                    <CountryDropdown
                      menuIsOpen={showCountryDropDown}
                      options={countryCodes?.list}
                      formatOptionLabel={formatOptionLabelV2}
                      wrapperClassName="shyftList"
                      dropDownHeight="180px"
                      placeholder="Search"
                      defaultValue={value}
                      onChange={(countryCode) => {
                        onChange(countryCode.code);
                        setShowCountryDropDown(false);
                      }}
                      closeMenuOnSelect
                      variant={variant}
                    />
                  </DropDown>
                </CountryPlaceholder>
              )}
            />
            <Input
              ref={ref}
              className="phoneInput"
              inputPadding="3px 16px 3px 100px"
              type="text"
              placeholder={mobilePlaceholder}
              pattern="[0-9]*"
              inputMode="decimal"
              name={name}
              value={value}
              onChange={(e) => {
                if (e.target.value === "" || isValidMobile(e.target.value)) {
                  onChange(e);
                }
              }}
              onBlur={(e) => {
                onBlur(e).then((e) => {
                  const isValidNumber = matchCountryRegex(
                    values?.[`${name}CountryCode`],
                    value
                  );
                  if (!errors?.[name] && isValidNumber) handleBlur(e);
                });
              }}
              autoComplete="off"
              variant={variant}
              inputFontSize={inputFontSize}
              inputLineHeight={inputLineHeight}
            />
          </InputWrap>
          {error && (
            <ErrorUI
              hasBottomBorder
              error={error?.message}
              isNotAbsolute={isNotAbsolute}
            />
          )}
        </>
      )}
    />
  );

  if (variant === "v1") {
    return <></>;
  } else if (variant === "v2") {
    return (
      <InputWrapperV2>
        <InputGroupV2 hasBottomBorder showLabels={showLabels}>
          {inputController}
        </InputGroupV2>
      </InputWrapperV2>
    );
  } else {
    return (
      <InputWrapperV3>
        <InputGroupV3 hasBottomBorder showLabels={showLabels}>
          {inputController}
        </InputGroupV3>
      </InputWrapperV3>
    );
  }
};

export default PhoneInput;
